import { Loader2 } from 'lucide-react';
import { PortalDsAlert, PortalDsEmptyState } from 'src/components/portal-design-system';
import { UsageInsightsMetricCard, UsageInsightsMetricGrid } from 'src/features/usage-insights/components/usage-insights-metric-card';
import { ReportingApiClientsTable } from 'src/features/reporting-api-usage/components/reporting-api-clients-table';
import { ReportingApiFilterBar } from 'src/features/reporting-api-usage/components/reporting-api-filter-bar';
import {
  useGetReportingApiClientsQuery,
  useGetReportingApiSummaryQuery,
} from 'src/features/reporting-api-usage/endpoints/reporting-api-usage.endpoints';
import { useReportingApiUsageUrlParams } from 'src/features/reporting-api-usage/hooks/use-reporting-api-usage-url-params';
import {
  formatCount,
  formatRatePercent,
  getReportingApiUsageErrorMessage,
  isReportingApiUsageNotAuthorised,
} from 'src/features/reporting-api-usage/utils/reporting-api-usage.utils';
import { ApisClientDetailSheet } from './apis-client-detail-sheet';

export function ApisOverview() {
  const {
    days,
    selectedClientId,
    setDays,
    setSelectedClientId,
  } = useReportingApiUsageUrlParams();

  const summaryQuery = useGetReportingApiSummaryQuery({ days });
  const clientsQuery = useGetReportingApiClientsQuery({ days });

  const loading = summaryQuery.isLoading || clientsQuery.isLoading;
  const clients = Array.isArray(clientsQuery.data?.clients) ? clientsQuery.data.clients : [];
  const selectedClient = clients.find((row) => row.client_id === selectedClientId);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  const authError = summaryQuery.error ?? clientsQuery.error;
  if ((summaryQuery.isError || clientsQuery.isError) && isReportingApiUsageNotAuthorised(authError)) {
    return (
      <PortalDsEmptyState
        title="Not authorised"
        description="API usage is available to Invisibly staff only."
      />
    );
  }

  if (summaryQuery.isError || !summaryQuery.data || clientsQuery.isError || !clientsQuery.data) {
    return (
      <PortalDsAlert variant="danger">
        {getReportingApiUsageErrorMessage(summaryQuery.error ?? clientsQuery.error)}
      </PortalDsAlert>
    );
  }

  const summary = summaryQuery.data;

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <ReportingApiFilterBar
        days={days}
        onDaysChange={setDays}
      />

      <UsageInsightsMetricGrid>
        <UsageInsightsMetricCard
          label="Requests today"
          value={formatCount(summary.requests_today)}
        />
        <UsageInsightsMetricCard
          label={`Requests (${summary.window_days}d)`}
          value={formatCount(summary.requests_window)}
        />
        <UsageInsightsMetricCard
          label="Active keys"
          value={formatCount(summary.active_key_count)}
        />
        <UsageInsightsMetricCard
          label="Error rate"
          value={formatRatePercent(summary.error_rate)}
        />
        <UsageInsightsMetricCard
          label="Cache hit rate"
          value={formatRatePercent(summary.cache_hit_rate)}
        />
      </UsageInsightsMetricGrid>

      <ReportingApiClientsTable
        rows={clients}
        onSelectClient={setSelectedClientId}
      />

      <ApisClientDetailSheet
        open={Boolean(selectedClientId)}
        clientId={selectedClientId}
        extClientId={selectedClient?.ext_client_id}
        clientTitle={selectedClient?.client_title}
        days={days}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedClientId(undefined);
          }
        }}
      />
    </div>
  );
}
